import iconsPng from '@/assets/img/png/icons';
import svgIcons from '@/assets/svg/icons';
import SvgIcon from '@/components/atoms/SvgIcon';
import CustomButton from '@/components/atoms/CustomButton';
import WellnessStateCircleMobile from '@/components/molecules/WellnessStateCircle/WellnessStateCircleMobile';
import LoadingPage from '@/components/templates/LoadingPage';
import MobileFirstPageTemplate from '@/components/templates/MobileFirstPageTemplate';
import { EMPTY_WELLNESS_DOMAINS, MIN_SCREENER_SUBMISSIONS_TO_SHOW_ORG_DATA } from '@/constants';
import { MOBILE_DEFAULT_PADDING } from '@/constants/ui';
import useScreenerResultsByToken from '@/hooks/publicscreener/useScreenerResultsByToken';
import useIsWeb from '@/hooks/useIsWeb';
import useToast from '@/hooks/useToast';
import { Center, Image, Text, VStack, HStack } from '@chakra-ui/react';
import { createFileRoute } from '@tanstack/react-router';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { motion } from 'framer-motion';

import React, { useCallback, useState } from 'react';
import colors from '@/theme/colors';
import useScrollDirection from '@/hooks/useScrollDirection';
import OrganizationWholenessRingExample from '@/modules/try-screener/OrganizationWholenessRingExample';

const TryScreenerResultPage = () => {
  const { _splat } = Route.useParams();
  const token = _splat;
  const tokenFromEmailLink = token;

  const isWeb = useIsWeb();
  const toast = useToast();
  const scrollDir = useScrollDirection();

  const { data: tokenScreenerData, isSuccess, isLoading } = useScreenerResultsByToken(tokenFromEmailLink);

  const personalDomainsFinal = tokenScreenerData?.personal.domains;
  const personalTotalScoreFinal = tokenScreenerData?.personal.average;

  const users = tokenScreenerData?.organization.users;

  const showOrganizationScore =
    tokenScreenerData?.organization.available && users && users >= MIN_SCREENER_SUBMISSIONS_TO_SHOW_ORG_DATA;
  const showExampleRing = !showOrganizationScore;

  const shareLink = tokenScreenerData?.shareLink;

  const [copied, setCopied] = useState(false);
  const onCopyUrlToClipboard = () => {
    setCopied(true);

    toast({
      title: 'URL copied to clipboard',
      description: shareLink,
      status: 'success',
    });

    setTimeout(() => setCopied(false), 5000);
  };

  const onShareScreenerClick = useCallback(() => {
    navigator.share({
      url: shareLink,
      title: 'Share MyOmnia Screener',
      text: 'Try the MyOmnia Screener and see your wellness score!',
    });
  }, [isWeb]);

  return (
    <MobileFirstPageTemplate>
      {isLoading && <LoadingPage />}
      {isSuccess && (
        <VStack position={'relative'} maxWidth={'100%'} width={'100%'} overflow={'hidden'}>
          <Center
            position={'fixed'}
            bottom={12}
            zIndex={5}
            px={MOBILE_DEFAULT_PADDING}
            width={'100%'}
            alignItems={'center'}
            justifyContent={'center'}
          >
            <motion.div
              style={{ width: '100%' }}
              initial={{ y: 0 }}
              animate={{ y: scrollDir === 'down' ? 100 : 0 }}
              transition={{ duration: 0.3 }}
            >
              <Center>
                <CustomButton
                  backgroundColor="primary.500"
                  labelColor="extra.white"
                  label="Share the Screener"
                  onClick={onShareScreenerClick}
                  width={'100%'}
                  style={{
                    maxWidth: '360px',
                  }}
                />
              </Center>
            </motion.div>
          </Center>
          <Text variant={'urbanistSemiBold'} mt={'10px'}>
            Kudos for completing the screener!
          </Text>
          <Center marginTop={'20px'}>
            <Image alt="add-member" src={iconsPng.handshake} width={100} height={100} />
          </Center>
          <Text variant={'loraExtraSmallTitle'} marginY={'20px'}>
            Your Personal Wholeness Ring
          </Text>
          <WellnessStateCircleMobile
            domainsData={personalDomainsFinal ?? EMPTY_WELLNESS_DOMAINS}
            totalScore={personalTotalScoreFinal ?? 0}
            showPercentageTextOnTopOfCircle
            circleSvgId="personal-circle-svg"
          />
          <Text
            variant={'urbanistSemiBold'}
            color={'extra.white'}
            backgroundColor={'secondary.500'}
            padding={'12px'}
            borderRadius={'8px'}
            maxWidth={'360px'}
            mt={'40px'}
            width={'100%'}
          >
            Your results have been added to your organization’s MyOmnia Wholeness Ring.
          </Text>
          {showOrganizationScore && (
            <VStack mt={'42px'}>
              <VStack marginBottom={'32px'} gap={'10px'}>
                <Text variant={'loraExtraSmallTitle'}>Organization’s Wholeness Ring</Text>
                <Text variant={'urbanistSemiBoldRegular'}>{users} co-workers have taken the screener.</Text>
              </VStack>
              <WellnessStateCircleMobile
                domainsData={tokenScreenerData.organization?.domains ?? EMPTY_WELLNESS_DOMAINS}
                totalScore={tokenScreenerData.organization?.average ?? 0}
                showPercentageTextOnTopOfCircle
                circleSvgId="token-org-circle-svg"
              />
              <Text variant={'loraExtraSmallTitle'} maxWidth={'260px'} marginY={'30px'}>
                Share the Screener with your Co-Workers
              </Text>
              <HStack
                borderRadius={'8px'}
                border={'border: 1px solid #0025484D'}
                backgroundColor={'extra.white'}
                boxShadow="0px 2px 4px 0px #0041790D"
                justifyContent={'space-between'}
                paddingX={'18px'}
                paddingY={'11px'}
                gap={'10px'}
                marginBottom={'220px'}
                width={'100%'}
                marginX={MOBILE_DEFAULT_PADDING}
                maxWidth={'360px'}
              >
                <VStack alignItems={'start'} gap={'4px'} width={'100%'}>
                  <Text variant={'urbanistExtraBoldSmall'} color={'text.mediumGray'}>
                    YOUR ORGANIZATION’S UNIQUE URL
                  </Text>
                  <Text
                    variant={'urbanistSemiBoldRegular'}
                    maxWidth={'340px'}
                    noOfLines={1}
                    style={{ lineBreak: 'anywhere' }}
                  >
                    {shareLink}
                  </Text>
                </VStack>
                <VStack>
                  {copied ? (
                    <SvgIcon
                      iconPath={svgIcons.check}
                      color={colors.text.mediumBlue}
                      size={14}
                      boxWidth={12}
                      boxHeight={12}
                    />
                  ) : (
                    <CopyToClipboard text={shareLink ?? ''} onCopy={onCopyUrlToClipboard}>
                      <button>
                        <SvgIcon iconPath={svgIcons.copy} color={colors.text.mediumBlue} size={18} />
                      </button>
                    </CopyToClipboard>
                  )}
                </VStack>
              </HStack>
            </VStack>
          )}
          {showExampleRing && (
            <VStack mt={'42px'}>
              <OrganizationWholenessRingExample
                atLeastCounter={MIN_SCREENER_SUBMISSIONS_TO_SHOW_ORG_DATA - (users ?? 0)}
              />
              <Text variant={'loraExtraSmallTitle'} maxWidth={'260px'} marginY={'30px'}>
                Share the Screener with your Co-Workers
              </Text>
              <HStack
                borderRadius={'8px'}
                border={'border: 1px solid #0025484D'}
                backgroundColor={'extra.white'}
                boxShadow="0px 2px 4px 0px #0041790D"
                justifyContent={'space-between'}
                paddingX={'18px'}
                paddingY={'11px'}
                gap={'10px'}
                marginBottom={'220px'}
                width={'100%'}
                marginX={MOBILE_DEFAULT_PADDING}
                maxWidth={'360px'}
              >
                <VStack alignItems={'start'} gap={'4px'} width={'100%'}>
                  <Text variant={'urbanistExtraBoldSmall'} color={'text.mediumGray'}>
                    YOUR ORGANIZATION’S UNIQUE URL
                  </Text>
                  <Text
                    variant={'urbanistSemiBoldRegular'}
                    maxWidth={'340px'}
                    noOfLines={1}
                    style={{ lineBreak: 'anywhere' }}
                  >
                    {shareLink}
                  </Text>
                </VStack>
                <VStack>
                  {copied ? (
                    <SvgIcon
                      iconPath={svgIcons.check}
                      color={colors.text.mediumBlue}
                      size={14}
                      boxWidth={12}
                      boxHeight={12}
                    />
                  ) : (
                    <CopyToClipboard text={shareLink ?? ''} onCopy={onCopyUrlToClipboard}>
                      <button>
                        <SvgIcon iconPath={svgIcons.copy} color={colors.text.mediumBlue} size={18} />
                      </button>
                    </CopyToClipboard>
                  )}
                </VStack>
              </HStack>
            </VStack>
          )}
        </VStack>
      )}
    </MobileFirstPageTemplate>
  );
};

export default TryScreenerResultPage;

export const Route = createFileRoute('/try-screener/result/$')({
  staticData: { title: 'Screener Results' },
  component: TryScreenerResultPage,
});
